import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import type { MapMarker } from '../api'
import { label } from '../vocab'

/**
 * Every incident that carries a coordinate, placed.
 *
 * ## Why a projected scatter and not a basemap
 *
 * The reading is relative — is this one street or the whole county, are the critical ones
 * together or scattered. Tiles answer none of that and cost a token, a network dependency
 * and a second of load. The projection is equirectangular with a cos(latitude) correction
 * on x, which over a county is indistinguishable from anything more careful and without
 * which the map is visibly stretched east–west near the equator.
 *
 * The domain is the 5th–95th percentile rather than min/max: one incident mis-keyed into
 * another province otherwise squashes everything that matters into a corner. Points outside
 * the frame are pinned to the edge in a dashed square and counted, never dropped.
 *
 * ## Co-located incidents are CLUSTERED, not stacked
 *
 * Reports are filed against a place — a polling station, a market, a road — so a dozen
 * incidents routinely share one coordinate to five decimal places. Drawn as individual dots
 * they land exactly on top of each other and the map shows one incident where there are
 * thirteen, which is worse than showing nothing: it reads as a quiet location. So identical
 * coordinates become one marker carrying its count, sized by the group and coloured by the
 * WORST severity in it — because the reason to look at a cluster is its most serious member,
 * not its average.
 *
 * ## Colour is severity, and it is allowed to be
 *
 * This is an ordered scale (LOW → CRITICAL), drawn on the ordinal ramp, and every dot is
 * also sized by it — so severity survives greyscale, colour blindness and print. The legend
 * names all four steps, and incidents with no coordinate are reported in text rather than
 * placed somewhere plausible.
 */
const SEVERITY_STEP: Record<string, { ramp: string; r: number; rank: number }> = {
  LOW:      { ramp: 'var(--ordinal-1)', r: 5, rank: 1 },
  MEDIUM:   { ramp: 'var(--ordinal-2)', r: 7, rank: 2 },
  HIGH:     { ramp: 'var(--ordinal-4)', r: 9, rank: 3 },
  CRITICAL: { ramp: 'var(--ordinal-5)', r: 12, rank: 4 },
}
const ORDER = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']

interface Cluster {
  key: string
  lat: number
  lon: number
  members: MapMarker[]
}

export function IncidentMap({
  markers, selected, onSelect, height = 400,
}: {
  markers: MapMarker[]
  selected: string | null
  onSelect: (id: string | null) => void
  height?: number
}) {
  const [hover, setHover] = useState<string | null>(null)
  const placed = useMemo(
    () => markers.filter((m) => m.latitude != null && m.longitude != null), [markers])
  const missing = markers.length - placed.length

  /*
     Four decimal places is about eleven metres — finer than any of these reports is, and
     coarse enough that two filings of the same event at the same place become one marker
     rather than two dots a pixel apart.
  */
  const clusters = useMemo(() => {
    const by = new Map<string, Cluster>()
    for (const m of placed) {
      const key = `${m.latitude!.toFixed(4)},${m.longitude!.toFixed(4)}`
      const at = by.get(key)
      if (at) at.members.push(m)
      else by.set(key, { key, lat: m.latitude!, lon: m.longitude!, members: [m] })
    }
    return [...by.values()]
  }, [placed])

  const geom = useMemo(() => {
    if (clusters.length === 0) return null
    const lats = clusters.map((c) => c.lat)
    const midLat = (Math.min(...lats) + Math.max(...lats)) / 2
    const kx = Math.cos((midLat * Math.PI) / 180) || 1
    const xs = clusters.map((c) => c.lon * kx)
    const span = (vs: number[]) => {
      const sorted = [...vs].sort((a, b) => a - b)
      const at = (q: number) => sorted[Math.min(sorted.length - 1,
        Math.max(0, Math.round(q * (sorted.length - 1))))]!
      const lo = at(0.05), hi = at(0.95)
      const d = hi - lo
      if (d < 1e-6) return { lo: lo - 0.01, hi: hi + 0.01 }
      return { lo: lo - d * 0.12, hi: hi + d * 0.12 }
    }
    return { x: span(xs), y: span(lats), kx }
  }, [clusters])

  if (placed.length === 0) {
    return (
      <div className="p-4 space-y-2">
        <p className="text-erp-sm text-muted-foreground">
          None of the {markers.length} incidents in this window carries a coordinate, so there
          is nothing to place. A location is captured on the report's own step, and an incident
          may legitimately have none — the reporter may not have been at the scene.
        </p>
        <p className="text-erp-xs text-muted-foreground/80">
          Placing them by their county centroid would draw a precision the register does not
          hold, which is the one thing a map must never do.
        </p>
      </div>
    )
  }

  const W = 720, H = height, PAD = 26
  const fx = (c: Cluster) => (c.lon * geom!.kx - geom!.x.lo) / (geom!.x.hi - geom!.x.lo)
  // Latitude rises northwards, screen y downwards. The subtraction is the flip; without it
  // the map is upside down and still looks plausible.
  const fy = (c: Cluster) => 1 - (c.lat - geom!.y.lo) / (geom!.y.hi - geom!.y.lo)
  const outside = (c: Cluster) => fx(c) < 0 || fx(c) > 1 || fy(c) < 0 || fy(c) > 1
  const clamp = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)
  const px = (c: Cluster) => PAD + clamp(fx(c)) * (W - PAD * 2)
  const py = (c: Cluster) => PAD + clamp(fy(c)) * (H - PAD * 2)
  const offFrame = clusters.filter(outside)

  /** The worst member decides the colour: a cluster is looked at for its most serious report. */
  const worst = (c: Cluster) => c.members.reduce((acc, m) => {
    const st = SEVERITY_STEP[m.severityCode] ?? SEVERITY_STEP.MEDIUM!
    return st.rank > acc.rank ? st : acc
  }, SEVERITY_STEP.LOW!)
  // Area, not radius, grows with the count — radius-scaling overstates a big group by the
  // square, which is the oldest bubble error there is.
  const radius = (c: Cluster) => worst(c).r + Math.sqrt(c.members.length - 1) * 5

  const drawn = [...clusters].sort((a, b) => worst(a).rank - worst(b).rank)
  const active = hover ?? selected
  const shown = clusters.find((c) => c.key === active)

  return (
    <div className="p-3 space-y-2">
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto select-none"
             role="list" aria-label="Incidents by location">
          <rect x={0} y={0} width={W} height={H} rx={8} className="fill-muted/30" />
          {[0.25, 0.5, 0.75].map((f) => (
            <g key={f} className="stroke-border/60" strokeWidth={1}>
              <line x1={PAD} x2={W - PAD} y1={PAD + f * (H - PAD * 2)} y2={PAD + f * (H - PAD * 2)} />
              <line y1={PAD} y2={H - PAD} x1={PAD + f * (W - PAD * 2)} x2={PAD + f * (W - PAD * 2)} />
            </g>
          ))}
          {drawn.map((c) => {
            const st = worst(c)
            const r = radius(c)
            const on = c.key === active
            const off = outside(c)
            const n = c.members.length
            return (
              <g key={c.key} role="listitem">
                {off && (
                  <rect x={px(c) - r - 3} y={py(c) - r - 3}
                        width={(r + 3) * 2} height={(r + 3) * 2} rx={2}
                        fill="none" className="stroke-foreground/40" strokeDasharray="2 2" />
                )}
                <circle
                  cx={px(c)} cy={py(c)} r={r}
                  fill={st.ramp} fillOpacity={0.5}
                  stroke={st.ramp} strokeWidth={on ? 3 : 2}
                  className="cursor-pointer"
                  tabIndex={0}
                  onMouseEnter={() => setHover(c.key)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(c.key)}
                  onBlur={() => setHover(null)}
                  onClick={() => onSelect(c.key === selected ? null : c.key)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onSelect(c.key) }}
                >
                  <title>
                    {n === 1
                      ? `${c.members[0]!.ref} — ${label(c.members[0]!.severityCode)}`
                      : `${n} incidents here, worst ${label(
                          c.members.reduce((a, m) => ((SEVERITY_STEP[m.severityCode]?.rank ?? 0)
                            > (SEVERITY_STEP[a.severityCode]?.rank ?? 0) ? m : a)).severityCode)}`}
                  </title>
                </circle>
                {/* The count is the point of a cluster, so it is printed rather than left to
                    be inferred from area — area is a bad channel for exact quantity. */}
                {n > 1 && (
                  <text x={px(c)} y={py(c)} textAnchor="middle" dominantBaseline="central"
                        className="pointer-events-none fill-foreground text-[11px] font-medium"
                        style={{ fontSize: 11 }}>
                    {n}
                  </text>
                )}
              </g>
            )
          })}
        </svg>
        {shown && (
          <div className="pointer-events-none absolute left-3 top-3 rounded-md border border-border bg-popover px-3 py-2 text-erp-xs text-popover-foreground shadow-md max-w-[320px]">
            <div className="font-medium">
              {shown.members.length === 1
                ? shown.members[0]!.ref
                : `${shown.members.length} incidents at this location`}
            </div>
            {shown.members[0]?.locationLabel && (
              <div className="text-muted-foreground">{shown.members[0].locationLabel}</div>
            )}
            <ul className="mt-1 space-y-0.5">
              {shown.members.slice(0, 8).map((m) => (
                <li key={m.id} className="tabular-nums">
                  <span className="font-mono">{m.ref}</span>{' — '}
                  {label(m.severityCode)} · {label(m.statusCode)}
                </li>
              ))}
              {shown.members.length > 8 && (
                <li className="text-muted-foreground">
                  and {shown.members.length - 8} more
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-erp-xs text-muted-foreground">
        {ORDER.map((code) => {
          const st = SEVERITY_STEP[code]!
          return (
            <span key={code} className="inline-flex items-center gap-1.5">
              <svg width={16} height={16} aria-hidden>
                <circle cx={8} cy={8} r={Math.min(st.r / 2 + 2, 7)}
                        fill={st.ramp} fillOpacity={0.55} stroke={st.ramp} strokeWidth={2} />
              </svg>
              {label(code)}
            </span>
          )
        })}
        <span>Colour is the worst severity in the group; the number is how many are there</span>
        {offFrame.length > 0 && (
          <span>
            {offFrame.length} outside this frame, pinned to the edge (dashed square)
          </span>
        )}
        {missing > 0 && (
          <span className={cn('text-amber-600 dark:text-amber-400')}>
            {missing} {missing === 1 ? 'incident has' : 'incidents have'} no coordinate
          </span>
        )}
      </div>
    </div>
  )
}
